import type { ReactNode } from 'react'

import { useUnit } from 'effector-react'
import { Link } from 'react-router-dom'

import BackIcon from '@/assets/icons/icon-back.svg'
import { Button } from '@/components/ui/button.tsx'
import { $creatorId } from '@/models/creators.ts'
import { classNames } from '@/utils/helpers'

interface PageHeaderProps {
  title?: string | JSX.Element
  description?: ReactNode
  titleOptions?: ReactNode
  optionComponents?: ReactNode
  className?: string
  titleClassName?: string
  backToPage?: { name: string; url: string }
}

export const PageHeader = ({
  title,
  titleOptions,
  optionComponents,
  className,
  description,
  titleClassName,
  backToPage,
}: PageHeaderProps) => {
  const creatorId = useUnit($creatorId)

  return (
    <div
      className={classNames(
        'mb-6 flex h-fit flex-col justify-start space-y-4 lg:flex-row lg:justify-between lg:space-y-0',
        backToPage ? 'items-bottom' : 'lg:items-center',
        className,
      )}
    >
      <div className="flex flex-col">
        {backToPage && (
          <Button
            type="button"
            variant="ghost"
            className="back-to-page w-fit p-0"
          >
            <BackIcon />
            <Link
              to={backToPage.url.replace(
                ':creatorId',
                creatorId?.toString() ?? '',
              )}
            >
              Back to {backToPage.name}
            </Link>
          </Button>
        )}
        {title && (
          <div className="flex items-center">
            <h1
              className={classNames(
                'text-title text-3xl font-semibold leading-none',
                titleClassName,
              )}
              data-testid="page-title"
            >
              {title}
            </h1>
            {titleOptions && (
              <span className={titleClassName}>{titleOptions}</span>
            )}
          </div>
        )}
        {description && (
          <p className="text-text-sub mr-4 mt-2 text-sm">{description}</p>
        )}
      </div>
      {optionComponents && (
        <div className="page-header flex flex-col gap-2 sm:flex-row sm:items-end">
          {optionComponents}
        </div>
      )}
    </div>
  )
}
